const BUILDER_CART_SECTIONS = ['cart-count-badge', 'offcanvas-cart'];
const builders = new WeakMap();

class ProductBuilder {
  constructor(element) {
    this.el = element;
    this.config = this._parseConfig(element.dataset.config);
    this.currentStep = 0;
    // selections shape: { [stepIndex]: Array<{ ...option, qty: number }> }
    this.selections = {};
    this.buildId = this._uuid();
    this._isAdding = false;
    this._mobileSummaryExpanded = false;
    this._init();
  }

  get isBundle() { return this.config.cart_mode === 'bundle'; }
  get allowQty() { return this.config.allow_quantity || false; }
  get allowMulti() { return this.config.allow_multi_select || false; }
  // Anything other than "box" is steps mode (one step on screen at a time).
  get isBoxLayout() { return this.config.layout === 'box'; }

  // A fills_slots step takes its size from the chosen base: one slot swaps on tap, more slots multi-select.
  // Before a base is chosen, it sizes itself from the largest base offered anywhere, so it doesn't start
  // out looking like a single-pick step.
  _allowMultiForStep(step) {
    const slots = step.fills_slots ? this._slotLayout() : null;
    if (slots) return slots.count > 1;
    if (step.fills_slots) {
      const maxAnywhere = this._maxSlotCountAnywhere();
      if (maxAnywhere) return maxAnywhere > 1;
    }
    return typeof step.allow_multi_select === 'boolean'
      ? step.allow_multi_select
      : this.allowMulti;
  }

  _allowQtyForStep(step) {
    const slots = step.fills_slots ? this._slotLayout() : null;
    if (slots) return slots.count > 1;
    if (step.fills_slots) {
      const maxAnywhere = this._maxSlotCountAnywhere();
      if (maxAnywhere) return maxAnywhere > 1;
    }
    return typeof step.allow_quantity === 'boolean'
      ? step.allow_quantity
      : this.allowQty;
  }

  // Base products set custom.builder_slot_layout as rows x columns ("1x2", "2x2");
  // a bare number is one row ("3" = three in a row). Anything else means no slots.
  _parseSlotLayout(value) {
    const match = String(value ?? '').trim().toLowerCase().match(/^(\d+)(?:\s*[x×]\s*(\d+))?$/);
    if (!match) return null;
    const rows = match[2] ? Number(match[1]) : 1;
    const cols = match[2] ? Number(match[2]) : Number(match[1]);
    return rows > 0 && cols > 0 ? { rows, cols, count: rows * cols } : null;
  }

  // The chosen base is the first selected option, outside fills_slots steps, that has a slot layout.
  _slotLayout() {
    for (let i = 0; i < this.config.steps.length; i++) {
      if (this.config.steps[i].fills_slots || !this._isStepVisible(i)) continue;
      for (const sel of this._stepSels(i)) {
        const layout = this._parseSlotLayout(sel.slot_layout);
        if (layout) return { ...layout, label: sel.label };
      }
    }
    return null;
  }

  // Where a keycap goes, shown under its line in the summary, cart and order: "Slot 1", "Slots 2-3"
  // (slots read left to right, top to bottom like the preview) or "Spare".
  _slotNote(step, sel) {
    if (this._sharesSlotOptions(step)) return 'Spare';
    if (!step.fills_slots || !this._slotLayout()) return '';
    const fills = this._slotFills();
    const first = fills.indexOf(sel) + 1;
    const last = fills.lastIndexOf(sel) + 1;
    if (!first) return '';
    return first === last ? `Slot ${first}` : `Slots ${first}-${last}`;
  }

  // Picks from fills_slots steps in slot order, one entry per unit of quantity.
  _slotFills() {
    return this.config.steps.flatMap((step, i) => (step.fills_slots && this._isStepVisible(i)
      ? this._stepSels(i).flatMap(sel => Array(sel.qty || 1).fill(sel))
      : []));
  }

  // The largest slot count offered by any option with a slot layout, anywhere in the builder.
  // Lets a fills_slots step size itself sensibly before any base has been picked yet.
  _maxSlotCountAnywhere() {
    let max = 0;
    this.config.steps.forEach(step => {
      (step.options || []).forEach(opt => {
        const layout = this._parseSlotLayout(opt.slot_layout);
        if (layout && layout.count > max) max = layout.count;
      });
    });
    return max > 0 ? max : null;
  }

  _slotPreviewHtml(modifier = '') {
    const layout = this._slotLayout();
    if (!layout) return '';
    const fills = this._slotFills().slice(0, layout.count);
    const slots = Array.from({ length: layout.count }, (_, i) => {
      const sel = fills[i];
      // Drawn rather than typed: Bungee's "+" sits well below centre, which shows in small slots.
      if (!sel) return '<div class="builder-slot"><span class="builder-slot__plus" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"><path d="M5 1.5v7M1.5 5h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span></div>';
      return `<div class="builder-slot is-filled">${sel.image_url
        ? `<img src="${sel.image_url}" alt="" loading="lazy">`
        : `<span class="builder-slot__label">${this._escapeHtml(sel.label)}</span>`}</div>`;
    }).join('');
    const names = fills.map(sel => sel.label).join(', ');
    return `<div class="builder-slot-preview ${modifier}">
      <div class="builder-slot-preview__meta">
        <span class="builder-slot-preview__base">${this._escapeHtml(layout.label)}</span>
        <span class="builder-slot-preview__count">${fills.length} of ${layout.count} filled</span>
      </div>
      <div class="builder-slot-preview__grid" style="--slot-cols: ${layout.cols}" role="img"
           aria-label="${this._escapeHtml(`${fills.length} of ${layout.count} slots filled${names ? `: ${names}` : ''}`)}">${slots}</div>
      ${this._slotNotice ? `<p class="builder-slot-preview__notice" role="status">${this._escapeHtml(this._slotNotice)}</p>` : ''}
    </div>`;
  }

  _selectionMin(step) {
    const slots = step.fills_slots ? this._slotLayout() : null;
    if (slots) return slots.count;
    if (step.selection_min == null || step.selection_min === '') return step.required ? 1 : 0;
    const configured = Number(step.selection_min);
    return Number.isFinite(configured) && configured >= 0
      ? Math.floor(configured)
      : (step.required ? 1 : 0);
  }

  _selectionMax(step) {
    const slots = step.fills_slots ? this._slotLayout() : null;
    if (slots) return slots.count;
    if (step.fills_slots) {
      const maxAnywhere = this._maxSlotCountAnywhere();
      if (maxAnywhere) return maxAnywhere;
    }
    if (!this._allowMultiForStep(step)) return 1;
    const configured = Number(step.selection_max);
    return Number.isFinite(configured) && configured > 0
      ? Math.max(this._selectionMin(step), Math.floor(configured))
      : Infinity;
  }

  _selectionCount(stepIndex) {
    return this._stepSels(stepIndex).reduce((sum, sel) => sum + (sel.qty || 1), 0);
  }

  _isStepComplete(stepIndex) {
    const step = this.config.steps[stepIndex];
    const count = this._selectionCount(stepIndex);
    if (!step.required && count === 0) return true;
    return count >= this._selectionMin(step) && count <= this._selectionMax(step);
  }

  // Multi-select steps stop at their limit; single-select steps always let a new pick replace the old one.
  _isPickBlocked(stepIndex, variantId) {
    const step = this.config.steps[stepIndex];
    if (!this._allowMultiForStep(step)) return false;
    const isSelected = this._stepSels(stepIndex).some(sel => sel.variant_id === variantId);
    return !isSelected && this._selectionCount(stepIndex) >= this._selectionMax(step);
  }

  _parseConfig(raw) {
    const config = JSON.parse(raw);
    config.steps = config.steps
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order);
    return config;
  }

  _uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  _formatMoney(cents) {
    return window.theme?.formatMoneyWithoutTrailingZeros(cents)
      ?? window.Shopify?.formatMoney(cents)
      ?? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(cents / 100);
  }

  _priceOrIncluded(step, cents) {
    if (this.isBundle && step.required) return 'Included';
    if (this.isBundle && !step.required) return '+' + this._formatMoney(cents);
    return this._formatMoney(cents);
  }

  _optionPriceHtml(step, opt) {
    const isIncluded = this.isBundle && step.required;
    if (isIncluded) return '<span class="product-card-price-final">Included</span>';
    const price = this.isBundle && !step.required
      ? `+${this._formatMoney(opt.price)}`
      : this._formatMoney(opt.price);
    if (opt.compare_at_price > opt.price) {
      return `<s class="product-card-price-compare">${this._formatMoney(opt.compare_at_price)}</s> <span class="product-card-price-final product-price-final-sale">${price}</span>`;
    }
    return `<span class="product-card-price-final">${price}</span>`;
  }

  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // selections[i] is always an array (or undefined)
  _stepSels(stepIndex) { return this.selections[stepIndex] || []; }

  _runningTotal() {
    const allSels = Object.entries(this.selections)
      .filter(([i]) => this._isStepVisible(+i))
      .flatMap(([i, sels]) => sels.map(sel => ({ step: this.config.steps[+i], sel })));

    if (this.isBundle) {
      const optionalExtra = allSels
        .filter(({ step }) => !step.required)
        .reduce((sum, { sel }) => sum + sel.price * (sel.qty || 1), 0);
      return (this.config.base_price || 0) + optionalExtra;
    }
    return allSels.reduce((sum, { sel }) => sum + sel.price * (sel.qty || 1), 0);
  }

  _completedPayload(items) {
    return {
      builder_name: String(this.config.title || '').slice(0, 100) || null,
      item_count: items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0),
      line_count: items.length,
      total_cents: this._runningTotal()
    };
  }

  _isVisible(showIf) {
    if (!showIf) return true;
    const parentIdx = this.config.steps.findIndex(s => s.sort_order === showIf.parent_sort_order);
    if (parentIdx === -1) return true;
    const sels = this._stepSels(parentIdx);
    return sels.some(s => showIf.variant_ids.includes(s.variant_id));
  }

  _isStepVisible(index) { return this._isVisible(this.config.steps[index].show_if); }
  _isOptionVisible(opt) { return this._isVisible(opt.show_if); }

  _nextVisibleStep(fromIndex) {
    for (let i = fromIndex + 1; i < this.config.steps.length; i++) {
      if (this._isStepVisible(i)) return i;
    }
    return null;
  }

  _prevVisibleStep(fromIndex) {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (this._isStepVisible(i)) return i;
    }
    return null;
  }

  _canProceed() {
    return this._isStepComplete(this.currentStep);
  }

  _allRequiredComplete() {
    return this.config.steps.every((step, i) => {
      if (!this._isStepVisible(i)) return true;
      return this._isStepComplete(i);
    });
  }

  // The first visible required step that isn't complete yet - used for box-layout status text.
  _firstIncompleteStep() {
    for (let i = 0; i < this.config.steps.length; i++) {
      if (!this._isStepVisible(i)) continue;
      const step = this.config.steps[i];
      if (step.required && !this._isStepComplete(i)) return i;
    }
    return null;
  }

  _visibleSelections() {
    return this.config.steps
      .flatMap((step, i) => {
        if (!this._isStepVisible(i)) return [];
        return this._stepSels(i).map(sel => ({ step, sel }));
      });
  }

  _selectedItemCount(selections = this._visibleSelections()) {
    return selections.reduce((sum, { sel }) => sum + (sel.qty || 1), 0);
  }

  _statusBadge(opt, discountPct) {
    if (!opt.available) return `<span class="badge product-sold-out-badge">Sold out</span>`;
    const discountPercentage = opt.compare_at_price > opt.price
      ? (opt.compare_at_price - opt.price) / opt.compare_at_price * 100
      : 0;
    if (opt.on_sale && discountPercentage >= 5) return `<span class="product-card-alt__badge product-card-alt__badge--sale" data-discount-type="percentage">-${discountPct}%</span>`;
    return '';
  }

  _parentStepFor(step) {
    const cond = step.options.find(opt => opt.show_if)?.show_if;
    return cond ? this.config.steps.find(s => s.sort_order === cond.parent_sort_order) : null;
  }

  // A non-slot step offering the same products as the fills_slots step, e.g. "Add Spare Keycaps".
  // Spares never fill slots, but their tiles zoom and trim names like the main keycap step.
  _sharesSlotOptions(step) {
    if (step.fills_slots || !step.options?.length) return false;
    const slotIds = new Set(this.config.steps.filter(s => s.fills_slots).flatMap(s => s.options.map(o => o.variant_id)));
    return slotIds.size > 0 && step.options.every(o => slotIds.has(o.variant_id));
  }

  // A base picker is any non-fills_slots step whose options carry a slot layout.
  _isBasePickerStep(step) {
    return !step.fills_slots && step.options.some(opt => this._parseSlotLayout(opt.slot_layout));
  }

  _slotLabelHtml(opt) {
    const layout = this._parseSlotLayout(opt.slot_layout);
    if (!layout) return '';
    return `<div class="builder-option-slot-label">${layout.count} ${layout.count === 1 ? 'slot' : 'slots'}</div>`;
  }

  _buildPartHtml(label, priceStr, imageUrl = null, compareAtStr = null, note = '') {
    const priceHtml = compareAtStr
      ? `<s class="product-card-price-compare">${compareAtStr}</s> <span class="product-price-final-sale">${priceStr}</span>`
      : priceStr;
    return `<div class="cart-build-part ${imageUrl ? '' : 'cart-build-part--no-image'}">
      ${imageUrl ? `<div class="cart-build-part-image"><img src="${imageUrl}" alt="${label}" loading="lazy"></div>` : ''}
      <span>${label}${note ? `<small class="cart-build-part-note">${note}</small>` : ''}</span>
      <span class="cart-build-part-price">${priceHtml}</span>
    </div>`;
  }

  _setQty(stepIndex, variantId, delta, numEl = null) {
    const step = this.config.steps[stepIndex];
    const sels = this.selections[stepIndex];
    if (!sels) return;
    const sel = sels.find(s => s.variant_id === variantId);
    if (!sel) return;
    const currentCount = this._selectionCount(stepIndex);
    if (delta > 0 && currentCount >= this._selectionMax(step)) return;
    const nextQty = (sel.qty || 1) + delta;
    if (nextQty <= 0) {
      this.selections[stepIndex] = sels.filter(s => s.variant_id !== variantId);
      if (this.selections[stepIndex].length === 0) delete this.selections[stepIndex];
      this._render();
      return;
    }
    sel.qty = nextQty;
    if (numEl) numEl.textContent = sel.qty;
    this._renderSummary();
    this._renderMobileSummary();
    this._renderTotalBar();
  }

  _reset() {
    this.buildId = this._uuid();
    this.selections = {};
    this.currentStep = 0;
    this._isAdding = false;
    this.el.classList.remove('product-builder--added');
    this._render();
  }

  // "?pick=<variant id>" (from a keycap page's "Build a clicker" link) starts the build with that option picked.
  _preselect(variantId) {
    const i = this.config.steps.findIndex(s => s.options.some(o => o.variant_id === variantId && o.available));
    if (i < 0) return false;
    this._selectOption(i, this.config.steps[i].options.find(o => o.variant_id === variantId));
    return true;
  }

  _init() {
    this._progressEl = this.el.querySelector('[data-builder-progress]');
    this._stepIntroEl = this.el.querySelector('[data-builder-step-intro]');
    this._descriptionStepsEl = this.el.querySelector('[data-builder-description-steps]');
    this._stepEl = this.el.querySelector('[data-builder-step]');
    this._totalBarEl = this.el.querySelector('[data-builder-total-bar]');
    this._summaryEl = this.el.querySelector('[data-builder-summary]');
    this._mobileSummaryEl = this.el.querySelector('[data-builder-mobile-summary]');
    this.el.classList.toggle('product-builder--box', this.isBoxLayout);
    this._setStickySummaryOffset();
    if (this.isBoxLayout) {
      this._setStickySlotPreviewOffset();
      this._boxResizeHandler = () => {
        this._stepEl?.querySelectorAll('[data-builder-row]').forEach(row => this._updateScrollRow(row));
      };
      window.addEventListener('resize', this._boxResizeHandler);
    }
    const pick = Number(new URLSearchParams(window.location.search).get('pick'));
    if (pick) this._preselect(pick);
    this._render();
  }

  _setStickySummaryOffset() {
    if (!this._summaryEl) return;

    this._summaryOffsetUpdate = () => {
      if (!window.matchMedia('(min-width: 769px)').matches) {
        this._summaryEl.style.removeProperty('--builder-summary-sticky-top');
        return;
      }

      const headerGroup = document.querySelector('#header-group.sticky-top') || document.querySelector('#header-group');
      const headerHeight = headerGroup && !headerGroup.classList.contains('hide')
        ? headerGroup.getBoundingClientRect().height
        : 0;

      this._summaryEl.style.setProperty('--builder-summary-sticky-top', `${headerHeight + 20}px`);
    };

    this._summaryOffsetUpdate();
    window.addEventListener('scroll', this._summaryOffsetUpdate, { passive: true });
    window.addEventListener('resize', this._summaryOffsetUpdate);
  }

  // Box layout, phones only: the inline slot preview (.builder-slot-preview--inline) is
  // pinned via `position: sticky` just below the site header (see _product-builder.scss),
  // so it needs the current header height on hand. Mirrors _setStickySummaryOffset's
  // approach above, just for the opposite breakpoint and a different CSS var. Set on the
  // root element (not the preview itself) because box mode re-renders that markup on every
  // pick - a custom property on a stable ancestor keeps working across those re-renders.
  _setStickySlotPreviewOffset() {
    const headerGroup = document.querySelector('#header-group.sticky-top') || document.querySelector('#header-group');
    // Pin just below wherever the header's bottom edge actually is, so the strip follows the
    // header as it slides away and back instead of keeping a height measured mid-scroll.
    this._slotOffsetUpdate = () => {
      if (!window.matchMedia('(max-width: 768px)').matches) {
        this.el.style.removeProperty('--builder-slot-sticky-top');
        return;
      }
      const headerBottom = headerGroup ? Math.max(0, headerGroup.getBoundingClientRect().bottom) : 0;
      this.el.style.setProperty('--builder-slot-sticky-top', `${Math.round(headerBottom)}px`);
      // While pinned under the nav the strip squares off its top (see .is-stuck in the styles).
      const strip = this.el.querySelector('.builder-slot-preview--inline');
      if (strip) strip.classList.toggle('is-stuck', strip.getBoundingClientRect().top <= headerBottom + 1);
    };
    // Re-run after each render too: a pick rebuilds the strip, which drops its is-stuck class.
    this._updateStickySlot = this._slotOffsetUpdate;

    this._slotOffsetUpdate();
    window.addEventListener('scroll', this._slotOffsetUpdate, { passive: true });
    window.addEventListener('resize', this._slotOffsetUpdate);
    if (headerGroup) {
      // The header can show or hide after scrolling stops, so re-measure when it changes or finishes moving.
      this._slotHeaderGroup = headerGroup;
      headerGroup.addEventListener('transitionend', this._slotOffsetUpdate);
      this._slotHeaderObserver = new MutationObserver(this._slotOffsetUpdate);
      this._slotHeaderObserver.observe(headerGroup, { attributes: true, attributeFilter: ['class', 'style'] });
    }
  }

  destroy() {
    if (this._boxResizeHandler) window.removeEventListener('resize', this._boxResizeHandler);
    if (this._summaryOffsetUpdate) {
      window.removeEventListener('scroll', this._summaryOffsetUpdate);
      window.removeEventListener('resize', this._summaryOffsetUpdate);
    }
    if (this._slotOffsetUpdate) {
      window.removeEventListener('scroll', this._slotOffsetUpdate);
      window.removeEventListener('resize', this._slotOffsetUpdate);
    }
    if (this._slotHeaderGroup && this._slotOffsetUpdate) {
      this._slotHeaderGroup.removeEventListener('transitionend', this._slotOffsetUpdate);
    }
    this._slotHeaderObserver?.disconnect();
    this._updateStickySlot = null;

    const modal = document.getElementById('builder-info-modal');
    if (modal?._owner === this) this._closeInfoModal(false);
  }

  _render() {
    // Box mode re-renders the whole stack on every pick; hold the scroll position steady.
    const scrollBefore = this.isBoxLayout && typeof window !== 'undefined' ? window.scrollY : null;
    this._renderProgress();
    this._renderStep();
    this._renderTotalBar();
    this._renderSummary();
    this._renderMobileSummary();
    if (scrollBefore !== null && window.scrollY !== scrollBefore) {
      window.scrollTo(0, scrollBefore);
    }
    if (this._updateStickySlot) this._updateStickySlot();
  }

  _openInfoModal(step, opt, opener) {
    let modal = document.getElementById('builder-info-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'builder-info-modal';
      modal.className = 'builder-info-modal';
      modal.innerHTML = `<div class="builder-info-modal__backdrop"></div><div class="builder-info-modal__panel" role="dialog" aria-modal="true" aria-labelledby="builder-info-modal-title"></div>`;
      document.body.appendChild(modal);
      modal.querySelector('.builder-info-modal__backdrop').addEventListener('click', () => modal._close?.());
      document.addEventListener('keydown', event => {
        if (!modal.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          modal._close?.();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...modal.querySelectorAll('.builder-info-modal__panel a[href], .builder-info-modal__panel button:not([disabled]), .builder-info-modal__panel input:not([disabled]), .builder-info-modal__panel select:not([disabled]), .builder-info-modal__panel textarea:not([disabled]), .builder-info-modal__panel [tabindex]:not([tabindex="-1"])')];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      });
    }

    modal._owner = this;
    modal._opener = opener;
    modal._close = () => this._closeInfoModal();

    const dimsHtml = opt.dimensions_html
      ? `<div class="builder-info-modal__dims">
          <h4 class="builder-info-modal__dims-title">Approximate Dimensions</h4>
          ${opt.dimensions_html}
        </div>` : '';

    modal.querySelector('.builder-info-modal__panel').innerHTML = `
      <button class="builder-info-modal__close" type="button" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      ${opt.modal_image_url ? `<div class="builder-info-modal__image"><img src="${opt.modal_image_url}" alt="${this._escapeHtml(opt.label)}"></div>` : ''}
      <div class="builder-info-modal__body">
        <h3 id="builder-info-modal-title" class="builder-info-modal__title">${this._escapeHtml(opt.label)}</h3>
        ${opt.description ? `<div class="builder-info-modal__description rte">${opt.description}</div>` : ''}
        ${dimsHtml}
      </div>`;

    const closeButton = modal.querySelector('.builder-info-modal__close');
    closeButton.addEventListener('click', () => this._closeInfoModal());
    modal.classList.add('is-open');
    document.body.classList.add('builder-modal-open');
    closeButton.focus();
  }

  _closeInfoModal(restoreFocus = true) {
    const modal = document.getElementById('builder-info-modal');
    if (modal) modal.classList.remove('is-open');
    document.body.classList.remove('builder-modal-open');
    const opener = modal?._opener;
    if (modal) {
      modal._owner = null;
      modal._opener = null;
      modal._close = null;
    }
    if (restoreFocus && opener?.isConnected) opener.focus();
  }

  _renderProgress() {
    const bar = this._progressEl;
    if (!bar) return;

    if (this.isBoxLayout) {
      bar.innerHTML = '';
      bar.classList.add('d-none');
      return;
    }

    const visibleSteps = this.config.steps
      .map((step, i) => ({ step, configIdx: i }))
      .filter(({ configIdx }) => this._isStepVisible(configIdx));

    if (visibleSteps.length <= 1) {
      bar.innerHTML = '';
      bar.classList.add('d-none');
      return;
    }

    bar.classList.remove('d-none');
    bar.innerHTML = `<div class="builder-progress">
      ${visibleSteps.map(({ step, configIdx }, visiblePos) => {
        const isActive = configIdx === this.currentStep;
        const isComplete = this._isStepComplete(configIdx);
        const cls = isActive ? 'is-active' : isComplete ? 'is-complete' : '';
        const clickable = configIdx < this.currentStep || isComplete;
        return `<div class="builder-step-indicator ${cls} ${clickable ? 'is-clickable' : ''}" data-step-idx="${configIdx}">
          <div class="builder-step-number">${isComplete && !isActive ? '✓' : visiblePos + 1}</div>
          <div class="builder-step-label">${step.title}</div>
        </div>`;
      }).join('')}
    </div>`;

    bar.querySelectorAll('.builder-step-indicator.is-clickable').forEach(indicator => {
      indicator.addEventListener('click', () => {
        this._goToStep(parseInt(indicator.dataset.stepIdx));
      });
    });
  }

  // Shared multi-select hint, parameterised by step index so box mode can render every step at once.
  _multiHintHtml(step, stepIndex) {
    if (!this._allowMultiForStep(step) || step.help_text) return '';
    const selectionMin = this._selectionMin(step);
    const selectionMax = this._selectionMax(step);
    const selectionCount = this._selectionCount(stepIndex);
    const selectionTarget = selectionMin === selectionMax && Number.isFinite(selectionMax)
      ? selectionMax
      : null;
    return `<p class="builder-multi-hint">${selectionTarget ? `Choose ${selectionTarget}. ${selectionCount} of ${selectionTarget} selected.` : 'You can select more than one'}</p>`;
  }

  // Heading + help text + multi hint (+ slot preview/note). `includeSlotPreview` is true in steps
  // mode (the classic inline preview); box mode passes false since the preview is shown once,
  // separately, right after the base-picker section - showing it here too would duplicate it.
  _stepIntroHtml(stepIndex, { includeSlotPreview = true } = {}) {
    const step = this.config.steps[stepIndex];
    const helpHtml = step.help_text
      ? `<p class="builder-step-help">${this._escapeHtml(step.help_text).replace(/\n/g, '<br>')}</p>`
      : '';
    const multiHint = this._multiHintHtml(step, stepIndex);

    let slotSection = '';
    if (step.fills_slots) {
      if (includeSlotPreview) {
        slotSection = this._slotPreviewHtml('builder-slot-preview--inline');
      } else if (!this._slotLayout()) {
        slotSection = '<p class="builder-slot-pending-note">Pick a base to see where they go.</p>';
      }
    }

    return `
      <div class="builder-step-intro">
        <div class="builder-step-heading">
        <strong>${step.title}</strong>
        ${step.required ? '' : '<span class="builder-optional-badge">Optional</span>'}
        </div>
        ${helpHtml}
        ${multiHint}
        ${slotSection}
      </div>`;
  }

  // One option card. `ctx` carries the step-level values that would otherwise be recomputed
  // per card (allowQty, limitReached, whether this is the compact base-picker grid).
  // ponytail: keycap-specific - every option in a fills_slots step is a keycap, so the word adds nothing and
  // pushes names onto three lines on phones. Add a per-step setting if another slot builder needs other trimming.
  _displayLabel(step, opt) {
    return step.fills_slots || this._sharesSlotOptions(step) ? opt.label.replace(/\s+Keycap\b/i, '') : opt.label;
  }

  _optionCardHtml(ctx, opt) {
    const { step, stepIndex, stepSels, allowQty, limitReached, isBasePicker } = ctx;
    const label = this._displayLabel(step, opt);
    if (!this._isOptionVisible(opt)) return '';
    const selObj = stepSels.find(s => s.variant_id === opt.variant_id);
    const isSel = !!selObj;
    const unavail = !opt.available;
    const interactionDisabled = unavail || this._isPickBlocked(stepIndex, opt.variant_id);
    const qty = selObj ? (selObj.qty || 1) : 1;
    const discountPct = opt.on_sale && opt.compare_at_price > opt.price
      ? Math.round((opt.compare_at_price - opt.price) / opt.compare_at_price * 100)
      : 0;
    const badges = [
      opt.is_new ? `<span class="product-card-alt__badge product-card-alt__badge--new">NEW!</span>` : '',
      opt.is_pod ? `<span class="badge product-pod-badge">Made to Order</span>` : '',
      this._statusBadge(opt, discountPct)
    ].join('');
    const svgMinus = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="2" viewBox="0 0 10 2"><line x1="0" y1="1" x2="10" y2="1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    const svgPlus = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10"><line x1="5" y1="0" x2="5" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="0" y1="5" x2="10" y2="5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    const atcText = 'Add to bundle';
    const footer = isSel
      ? (allowQty
        ? `<div class="builder-option-footer">
             <div class="builder-option-qty">
               <button class="builder-qty-btn" type="button" data-qty-delta="-1" data-qty-variant="${opt.variant_id}" aria-label="Decrease">${svgMinus}</button>
               <span class="builder-qty-num">${qty}</span>
               <button class="builder-qty-btn" type="button" data-qty-delta="1" data-qty-variant="${opt.variant_id}" aria-label="Increase" ${limitReached ? 'disabled' : ''}>${svgPlus}</button>
             </div>
           </div>`
        : `<div class="builder-option-footer">
             <span class="builder-option-selected" aria-hidden="true">✓ Selected</span>
           </div>`)
      : (unavail
        ? ''
        : `<div class="builder-option-footer">
             <button class="builder-option-add-btn" type="button" aria-label="${atcText}: ${label}">${atcText}</button>
           </div>`);
    const infoBtn = step.show_product_info
      ? `<button class="builder-option-info-btn" type="button" data-info-variant="${opt.variant_id}" aria-label="Product info">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
         </button>`
      : '';
    const slotLabel = isBasePicker ? this._slotLabelHtml(opt) : '';
    return `<div class="builder-option-card ${isSel ? 'is-selected' : ''} ${interactionDisabled ? 'opacity-50' : ''}"
                 data-opt-variant="${opt.variant_id}"
                 role="button"
                 tabindex="${interactionDisabled ? '-1' : '0'}"
                 aria-pressed="${isSel ? 'true' : 'false'}"
                 ${interactionDisabled ? 'aria-disabled="true"' : ''}>
      <div class="builder-option-image">
        ${opt.image_url ? `<img src="${opt.image_url}" alt="${label}" loading="lazy">` : ''}
        ${badges}
        ${infoBtn}
      </div>
      <div class="builder-option-body">
        <div class="builder-option-label">${label}</div>
        ${slotLabel}
        <div class="builder-option-price">${this._optionPriceHtml(step, opt)}</div>
      </div>
      ${footer}
    </div>`;
  }

  // The options grid for one step - the compact base-picker layout when this step's options
  // carry slot layouts (and it isn't itself a fills_slots step).
  _optionsGridHtml(stepIndex) {
    const step = this.config.steps[stepIndex];
    const stepSels = this._stepSels(stepIndex);
    const allowQty = this._allowQtyForStep(step);
    const selectionCount = this._selectionCount(stepIndex);
    const selectionMax = this._selectionMax(step);
    const limitReached = selectionCount >= selectionMax;

    const allHidden = step.options.length > 0 && step.options.every(opt => !this._isOptionVisible(opt));
    const parentStep = allHidden ? this._parentStepFor(step) : null;
    // Compact sideways-scrolling cards are a box-layout thing only - steps mode keeps its
    // classic grid. Every non-fills_slots step gets the compact row (base picker and any
    // plain add-on step alike); isBasePicker narrows that to the one that also carries slot
    // layouts, purely so its cards get the "N slots" label.
    const isCompactRow = this.isBoxLayout && !allHidden && !step.fills_slots;
    const isBasePicker = isCompactRow && this._isBasePickerStep(step);
    // The fills_slots (keycap) step's own compact grid, box layout mobile only.
    const isSlotGrid = this.isBoxLayout && step.fills_slots;

    const options = step.sort_alphabetically
      ? [...step.options].sort((a, b) => a.label.localeCompare(b.label))
      : step.options;

    const ctx = { step, stepIndex, stepSels, allowQty, limitReached, isBasePicker };
    const optionsHtml = allHidden
      ? `<p class="builder-condition-message">Choose your <strong>${parentStep?.title || 'previous step'}</strong> first to see options here.</p>`
      : options.map(opt => this._optionCardHtml(ctx, opt)).join('');

    let gridClass = 'builder-options';
    if (isCompactRow) gridClass += ' builder-options--compact';
    if (isCompactRow && this._sharesSlotOptions(step)) gridClass += ' builder-options--zoom';
    if (isSlotGrid) gridClass += ' builder-options--slots';
    const grid = `<div class="${gridClass}" data-options-grid>${optionsHtml}</div>`;
    if (!isCompactRow) return grid;

    // Sideways rows get edge fades and arrows that show while there's more to swipe
    // (same cue as the mobile menu); the arrows are extras, so they stay out of the tab order.
    const arrow = (dir, path) => `<button class="builder-row-arrow builder-row-arrow--${dir}" type="button" data-row-arrow="${dir === 'left' ? -1 : 1}" tabindex="-1" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg></button>`;
    return `<div class="builder-row-viewport" data-builder-row>${grid}${arrow('left', 'M10 3 5 8l5 5')}${arrow('right', 'M6 3l5 5-5 5')}</div>`;
  }

  // One step's full section markup (heading + help text + grid) - reused for the current step
  // in steps mode and for every visible step stacked in box mode.
  _stepSectionHtml(stepIndex, { includeSlotPreview = true } = {}) {
    return this._stepIntroHtml(stepIndex, { includeSlotPreview }) + this._optionsGridHtml(stepIndex);
  }

  // Card clicks, qty buttons and info buttons, scoped to one step index - shared between steps
  // mode (the single current step) and box mode (every visible section).
  _bindStepHandlers(container, stepIndex) {
    const step = this.config.steps[stepIndex];

    container.querySelectorAll('[data-opt-variant]').forEach(card => {
      const selectCard = () => {
        const vid = parseInt(card.dataset.optVariant);
        const opt = step.options.find(o => o.variant_id === vid);
        if (!opt || !opt.available || this._isPickBlocked(stepIndex, vid)) return;
        this._selectOption(stepIndex, opt);
      };
      card.addEventListener('click', selectCard);
      card.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectCard();
      });
    });

    container.querySelectorAll('[data-qty-delta]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const numEl = btn.parentElement.querySelector('.builder-qty-num');
        this._setQty(stepIndex, parseInt(btn.dataset.qtyVariant), parseInt(btn.dataset.qtyDelta), numEl);
      });
    });

    container.querySelectorAll('[data-info-variant]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const vid = parseInt(btn.dataset.infoVariant);
        const opt = step.options.find(o => o.variant_id === vid);
        if (opt) this._openInfoModal(step, opt, e.currentTarget);
      });
    });
  }

  // Sideways rows (box layout on phones): restore the scroll position, wire the arrows,
  // and keep the can-scroll-left/right classes that drive the fades and arrows up to date.
  _initScrollRow(row, scrollLeft = 0) {
    const grid = row.querySelector('[data-options-grid]');
    if (!grid) return;
    grid.scrollLeft = scrollLeft;
    grid.addEventListener('scroll', () => this._updateScrollRow(row), { passive: true });
    row.querySelectorAll('[data-row-arrow]').forEach(btn => btn.addEventListener('click', () => {
      const card = grid.querySelector('.builder-option-card');
      const gap = Number.parseFloat(getComputedStyle(grid).columnGap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : grid.clientWidth * 0.8;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      grid.scrollBy({ left: Number(btn.dataset.rowArrow) * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
    }));
    this._updateScrollRow(row);
  }

  _updateScrollRow(row) {
    const grid = row.querySelector('[data-options-grid]');
    if (!grid) return;
    const max = grid.scrollWidth - grid.clientWidth;
    const tolerance = 8;
    row.classList.toggle('can-scroll-left', max > 2 && grid.scrollLeft > tolerance);
    row.classList.toggle('can-scroll-right', max > 2 && grid.scrollLeft < max - tolerance);
  }

  _renderStep() {
    const container = this._stepEl;
    if (!container) return;

    if (this.isBoxLayout) {
      if (this._stepIntroEl) this._stepIntroEl.innerHTML = '';

      // Re-rendering replaces the rows, so remember how far each sideways row was scrolled.
      const rowScroll = {};
      container.querySelectorAll('[data-step-section]').forEach(section => {
        const grid = section.querySelector('[data-builder-row] [data-options-grid]');
        if (grid) rowScroll[section.dataset.stepSection] = grid.scrollLeft;
      });

      const visibleIdxs = this.config.steps.map((_, i) => i).filter(i => this._isStepVisible(i));
      container.innerHTML = visibleIdxs.map(i => {
        const step = this.config.steps[i];
        const sectionHtml = this._stepSectionHtml(i, { includeSlotPreview: false });
        // Directly after the base picker, show the slot preview inline (mobile only - the
        // --inline modifier is already hidden at 769px and up).
        const afterBase = this._isBasePickerStep(step) ? this._slotPreviewHtml('builder-slot-preview--inline') : '';
        return `<section class="builder-section" data-step-section="${i}">${sectionHtml}</section>${afterBase}`;
      }).join('');

      visibleIdxs.forEach(i => {
        const section = container.querySelector(`[data-step-section="${i}"]`);
        if (!section) return;
        this._bindStepHandlers(section, i);
        const row = section.querySelector('[data-builder-row]');
        if (row) this._initScrollRow(row, rowScroll[i] || 0);
      });
      return;
    }

    const stepIndex = this.currentStep;
    const introHtml = this._stepIntroHtml(stepIndex, { includeSlotPreview: true });
    const gridHtml = this._optionsGridHtml(stepIndex);

    if (this._stepIntroEl) {
      this._stepIntroEl.innerHTML = introHtml;
      container.innerHTML = gridHtml;
    } else {
      container.innerHTML = introHtml + gridHtml;
    }

    this._bindStepHandlers(container, stepIndex);
  }

  _renderTotalBar() {
    const bar = this._totalBarEl;
    if (!bar) return;

    if (this.isBoxLayout) {
      const allDone = this._allRequiredComplete();
      bar.classList.remove('d-none');
      if (allDone) {
        bar.innerHTML = `<div class="builder-total-bar">
          <div class="builder-nav"><button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button></div>
        </div>`;
      } else {
        const idx = this._firstIncompleteStep();
        const label = idx !== null ? this._escapeHtml(this.config.steps[idx].title) : '';
        bar.innerHTML = `<div class="builder-total-bar">
          <span class="builder-total-bar__status">${label ? `Next: ${label}` : ''}</span>
        </div>`;
      }
      bar.querySelector('[data-builder-atc]')?.addEventListener('click', () => this._addToCart());
      return;
    }

    const nextIdx = this._nextVisibleStep(this.currentStep);
    const prevIdx = this._prevVisibleStep(this.currentStep);
    const isLast = nextIdx === null;
    const allDone = this._allRequiredComplete();

    const requiredTotal = this.config.steps.filter((s, i) => this._isStepVisible(i) && s.required).length;
    const requiredDone = this.config.steps.filter((s, i) => this._isStepVisible(i) && s.required && this._isStepComplete(i)).length;
    const visibleStepCount = this.config.steps.filter((_, i) => this._isStepVisible(i)).length;

    const step = this.config.steps[this.currentStep];
    const isOptional = !step.required;
    const hasSelection = this._stepSels(this.currentStep).length > 0;

    let forwardBtn;
    if (isOptional && !hasSelection && allDone) {
      forwardBtn = `<button class="btn btn-outline-secondary btn-sm" data-builder-skip>${isLast ? 'Skip & Add to Cart' : 'Skip this step →'}</button>`;
    } else if (isLast) {
      forwardBtn = allDone ? `<button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>` : '';
    } else {
      forwardBtn = `<button class="btn btn-primary btn-sm" data-builder-next ${this._canProceed() ? '' : 'disabled'}>Next →</button>`;
    }

    if (visibleStepCount <= 1 && !forwardBtn) {
      bar.innerHTML = '';
      bar.classList.add('d-none');
      return;
    }

    bar.classList.remove('d-none');
    bar.innerHTML = `<div class="builder-total-bar">
      ${visibleStepCount > 1 ? `<div class="builder-total-label">${requiredDone} of ${requiredTotal} required steps complete</div>` : ''}
      <div class="builder-nav">
        ${visibleStepCount > 1 ? `<button class="btn btn-outline-secondary btn-sm" data-builder-back ${prevIdx === null ? 'disabled' : ''}>← Back</button>` : ''}
        ${forwardBtn}
      </div>
    </div>`;

    bar.querySelector('[data-builder-back]')?.addEventListener('click', () => {
      if (prevIdx !== null) this._goToStep(prevIdx);
    });
    bar.querySelector('[data-builder-next]')?.addEventListener('click', () => {
      if (this._canProceed() && nextIdx !== null) this._goToStep(nextIdx);
    });
    bar.querySelector('[data-builder-skip]')?.addEventListener('click', () => {
      if (nextIdx !== null) this._goToStep(nextIdx);
      else this._addToCart();
    });
    bar.querySelector('[data-builder-atc]')?.addEventListener('click', () => this._addToCart());
  }

  _renderSummary() {
    const panel = this._summaryEl;
    if (!panel) return;

    const visibleSelections = this._visibleSelections();

    if (visibleSelections.length === 0 && !(this.isBundle && this.config.base_price > 0)) {
      const emptyText = this.config.summary_empty_text || 'Your build will appear here as you make selections.';
      panel.innerHTML = `<div class="builder-summary-empty">
        <span class="builder-summary-empty__kicker">Your picks</span>
        <h2>${this._escapeHtml(this.config.summary_title || 'Your Bundle')}</h2>
        <p>${this._escapeHtml(emptyText)}</p>
      </div>`;
      return;
    }

    const bundleBaseRow = this.isBundle && this.config.base_price > 0
      ? this._buildPartHtml(this.config.title, this._formatMoney(this.config.base_price))
      : '';

    const partsHtml = visibleSelections.map(({ step, sel }) => {
      const qty = sel.qty || 1;
      const label = qty > 1 ? `${sel.label} × ${qty}` : sel.label;
      const compareAt = sel.compare_at_price > sel.price ? this._formatMoney(sel.compare_at_price * qty) : null;
      return this._buildPartHtml(label, this._priceOrIncluded(step, sel.price * qty), sel.image_url, compareAt, this._slotNote(step, sel));
    }).join('');
    const visibleStepCount = this.config.steps.filter((_, i) => this._isStepVisible(i)).length;
    const allDone = this._allRequiredComplete();
    const showSummaryAtc = this.isBoxLayout ? allDone : (visibleStepCount <= 1 && allDone);
    const nextIdx = this._nextVisibleStep(this.currentStep);
    const prevIdx = this._prevVisibleStep(this.currentStep);
    const isLast = nextIdx === null;
    const isOptional = this.config.steps[this.currentStep]?.required === false;
    const canProceed = this._canProceed();

    let summaryNav = '';
    if (!this.isBoxLayout && visibleStepCount > 1) {
      const requiredTotal = this.config.steps.filter((s, i) => this._isStepVisible(i) && s.required).length;
      const requiredDone = this.config.steps.filter((s, i) => this._isStepVisible(i) && s.required && this._isStepComplete(i)).length;
      let fwdBtn;
      if (isOptional && !this._stepSels(this.currentStep).length && allDone) {
        fwdBtn = `<button class="btn btn-outline-secondary btn-sm" data-builder-skip>${isLast ? 'Skip & Add to Cart' : 'Skip →'}</button>`;
      } else if (isLast) {
        fwdBtn = allDone ? `<button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>` : '';
      } else {
        fwdBtn = `<button class="btn btn-primary btn-sm" data-builder-next ${canProceed ? '' : 'disabled'}>Next →</button>`;
      }
      summaryNav = `<div class="builder-summary-nav">
        ${requiredDone < requiredTotal ? `<div class="builder-summary-nav__label">${requiredDone} of ${requiredTotal} required steps complete</div>` : ''}
        <div class="builder-summary-nav__buttons">
          <button class="btn btn-outline-secondary btn-sm" data-builder-back ${prevIdx === null ? 'disabled' : ''}>← Back</button>
          ${fwdBtn}
        </div>
      </div>`;
    }

    panel.innerHTML = `<div class="cart-build-group builder-summary-group">
      <div class="cart-build-header">
        <span class="cart-build-name">${this._escapeHtml(this.config.summary_title || 'Your Bundle')}</span>
      </div>
      ${this._slotPreviewHtml()}
      <div class="cart-build-parts">${bundleBaseRow}${partsHtml}</div>
      <div class="builder-summary-total">
        <span class="builder-total-label">Total</span>
        <span class="builder-total-amount">${this._formatMoney(this._runningTotal())}</span>
      </div>
      ${showSummaryAtc ? `<div class="builder-summary-actions">
        <button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>
      </div>` : ''}
      ${summaryNav}
    </div>`;

    panel.querySelector('[data-builder-atc]')?.addEventListener('click', () => this._addToCart());
    panel.querySelector('[data-builder-back]')?.addEventListener('click', () => { if (prevIdx !== null) this._goToStep(prevIdx); });
    panel.querySelector('[data-builder-next]')?.addEventListener('click', () => { if (canProceed && nextIdx !== null) this._goToStep(nextIdx); });
    panel.querySelector('[data-builder-skip]')?.addEventListener('click', () => { if (nextIdx !== null) this._goToStep(nextIdx); else this._addToCart(); });
  }

  // Box layout tray: a row of small thumbnails, one per item, so the build visibly grows.
  _trayThumbsHtml(visibleSelections) {
    const units = visibleSelections.flatMap(({ step, sel }) => Array(sel.qty || 1).fill({ step, sel }));
    const maxShown = 5;
    const plus = '<span class="builder-tray-plus"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"><path d="M5 1.5v7M1.5 5h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>';
    const thumbs = units.slice(0, maxShown).map(({ step, sel }) => `<span class="builder-tray-thumb${step.fills_slots || this._sharesSlotOptions(step) ? ' builder-tray-thumb--zoom' : ''}">${sel.image_url ? `<img src="${sel.image_url}" alt="" loading="lazy">` : ''}</span>`);
    const more = units.length > maxShown ? `<span class="builder-tray-more">+${units.length - maxShown}</span>` : '';
    return `<div class="builder-tray-thumbs" aria-hidden="true">${thumbs.join(plus)}${more}</div>`;
  }

  _renderMobileSummary() {
    const tray = this._mobileSummaryEl;
    if (!tray) return;

    const visibleSelections = this._visibleSelections();
    const hasBase = this.isBundle && this.config.base_price > 0;
    const hasSelections = visibleSelections.length > 0 || hasBase;
    this.el.classList.toggle('product-builder--has-mobile-tray', hasSelections);

    if (!hasSelections) {
      this._mobileSummaryExpanded = false;
      this.el.classList.remove('product-builder--mobile-summary-expanded');
      tray.innerHTML = '';
      return;
    }

    const itemCount = this._selectedItemCount(visibleSelections) + (hasBase ? 1 : 0);
    const itemLabel = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    const summaryTitle = this._escapeHtml(this.config.summary_title || 'Your Bundle');
    const visibleStepCount = this.config.steps.filter((_, i) => this._isStepVisible(i)).length;
    const allDone = this._allRequiredComplete();
    const showAtc = this.isBoxLayout ? allDone : (visibleStepCount <= 1 && allDone);
    const nextIdx = this._nextVisibleStep(this.currentStep);
    const prevIdx = this._prevVisibleStep(this.currentStep);
    const isLast = nextIdx === null;
    const isOptional = this.config.steps[this.currentStep]?.required === false;
    const canProceed = this._canProceed();

    // Box layout shows "Next: ..." on the tray's top line instead of a second row, to keep the tray short.
    let trayNav = '';
    let trayCount = itemLabel;
    if (this.isBoxLayout) {
      const idx = showAtc ? null : this._firstIncompleteStep();
      if (idx !== null) trayCount = `Next: ${this._escapeHtml(this.config.steps[idx].title)}`;
    } else if (visibleStepCount > 1) {
      let fwdBtn;
      if (isOptional && !this._stepSels(this.currentStep).length && allDone) {
        fwdBtn = `<button class="btn btn-outline-secondary btn-sm" data-builder-skip>${isLast ? 'Skip & Add to Cart' : 'Skip →'}</button>`;
      } else if (isLast) {
        fwdBtn = allDone ? `<button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>` : '';
      } else {
        fwdBtn = `<button class="btn btn-primary btn-sm" data-builder-next ${canProceed ? '' : 'disabled'}>Next →</button>`;
      }
      trayNav = `<div class="builder-mobile-summary__nav">
        <button class="btn btn-outline-secondary btn-sm" data-builder-back ${prevIdx === null ? 'disabled' : ''}>Back</button>
        ${fwdBtn}
      </div>`;
    }

    const bundleBaseRow = hasBase
      ? this._buildPartHtml(this._escapeHtml(this.config.title), this._formatMoney(this.config.base_price))
      : '';
    const partsHtml = visibleSelections.map(({ step, sel }) => {
      const qty = sel.qty || 1;
      const label = qty > 1 ? `${sel.label} × ${qty}` : sel.label;
      const compareAt = sel.compare_at_price > sel.price ? this._formatMoney(sel.compare_at_price * qty) : null;
      return this._buildPartHtml(this._escapeHtml(label), this._priceOrIncluded(step, sel.price * qty), sel.image_url, compareAt, this._slotNote(step, sel));
    }).join('');

    this.el.classList.toggle('product-builder--mobile-summary-expanded', this._mobileSummaryExpanded);
    tray.innerHTML = `<div class="builder-mobile-summary ${this._mobileSummaryExpanded ? 'is-expanded' : ''}">
      <button class="builder-mobile-summary__toggle" type="button" data-builder-mobile-toggle aria-expanded="${this._mobileSummaryExpanded ? 'true' : 'false'}">
        <span class="builder-mobile-summary__meta">
          <span class="builder-mobile-summary__title">${summaryTitle}</span>
          <span class="builder-mobile-summary__count">${trayCount}</span>
        </span>
        <span class="builder-mobile-summary__price">${this._formatMoney(this._runningTotal())}</span>
        <span class="builder-mobile-summary__chevron-wrapper" aria-hidden="true">
          <span class="builder-mobile-summary__chevron"></span>
        </span>
      </button>
      <div class="builder-mobile-summary__details">
        ${this._slotPreviewHtml('builder-slot-preview--tray')}
        <div class="cart-build-parts">${bundleBaseRow}${partsHtml}</div>
      </div>
      ${this.isBoxLayout
        ? `<div class="builder-mobile-summary__actions builder-mobile-summary__actions--thumbs">
          ${this._trayThumbsHtml(visibleSelections)}
          ${showAtc ? `<button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>` : ''}
        </div>`
        : `<div class="builder-mobile-summary__actions">
        ${showAtc ? `<button class="btn btn-success btn-sm" data-builder-atc>${this._cartAddUncertain ? 'Check cart' : 'Add to Cart'}</button>` : trayNav}
      </div>`}
    </div>`;

    tray.querySelector('[data-builder-mobile-toggle]')?.addEventListener('click', () => {
      this._mobileSummaryExpanded = !this._mobileSummaryExpanded;
      this._renderMobileSummary();
    });
    tray.querySelector('[data-builder-atc]')?.addEventListener('click', () => this._addToCart());
    tray.querySelector('[data-builder-back]')?.addEventListener('click', () => { if (prevIdx !== null) this._goToStep(prevIdx); });
    tray.querySelector('[data-builder-next]')?.addEventListener('click', () => { if (canProceed && nextIdx !== null) this._goToStep(nextIdx); });
    tray.querySelector('[data-builder-skip]')?.addEventListener('click', () => { if (nextIdx !== null) this._goToStep(nextIdx); else this._addToCart(); });
  }

  _selectOption(stepIndex, option) {
    const step = this.config.steps[stepIndex];
    const current = this._stepSels(stepIndex);
    const existingIdx = current.findIndex(s => s.variant_id === option.variant_id);

    if (existingIdx >= 0) {
      // Deselect: allow if optional, or if multi-select and there are other selections
      if (!step.required || this._allowMultiForStep(step) || current.length > 1) {
        const updated = current.filter((_, i) => i !== existingIdx);
        if (updated.length === 0) delete this.selections[stepIndex];
        else this.selections[stepIndex] = updated;
      }
    } else if (this._allowMultiForStep(step)) {
      if (this._selectionCount(stepIndex) >= this._selectionMax(step)) return;
      this.selections[stepIndex] = [...current, { ...option, qty: 1 }];
    } else {
      // Single-select: replace
      this.selections[stepIndex] = [{ ...option, qty: current[0]?.variant_id === option.variant_id ? (current[0]?.qty || 1) : 1 }];
    }

    // Clear selections from hidden steps and hidden options within visible steps
    this.config.steps.forEach((s, i) => {
      if (!this._isStepVisible(i)) { delete this.selections[i]; return; }
      if (!this.selections[i]) return;
      this.selections[i] = this.selections[i].filter(sel => {
        const opt = s.options.find(o => o.variant_id === sel.variant_id);
        return opt && this._isOptionVisible(opt);
      });
      if (this.selections[i].length === 0) delete this.selections[i];
    });

    // Switching to a base with fewer slots takes the extra picks off the end. If the builder has a
    // spares step (the same keycaps again), they move there instead of disappearing.
    this._slotNotice = '';
    const spareIdx = this.config.steps.findIndex((s, i) => this._isStepVisible(i) && this._sharesSlotOptions(s));
    this.config.steps.forEach((s, i) => {
      if (!s.fills_slots || !this.selections[i]) return;
      const max = this._selectionMax(s);
      const removed = this._selectionCount(i) - max;
      if (removed <= 0) return;
      const takenOff = [];
      let extra = removed;
      while (extra > 0 && this.selections[i].length) {
        const last = this.selections[i][this.selections[i].length - 1];
        const take = Math.min(extra, last.qty || 1);
        last.qty = (last.qty || 1) - take;
        extra -= take;
        takenOff.push({ ...last, qty: take });
        if (last.qty <= 0) this.selections[i].pop();
      }
      if (this.selections[i].length === 0) delete this.selections[i];

      let moved = 0;
      if (spareIdx >= 0) {
        const spareStep = this.config.steps[spareIdx];
        const spares = this.selections[spareIdx] || (this.selections[spareIdx] = []);
        takenOff.forEach(item => {
          const qty = Math.min(item.qty, this._selectionMax(spareStep) - this._selectionCount(spareIdx));
          if (qty <= 0) return;
          const existing = spares.find(sel => sel.variant_id === item.variant_id);
          if (existing) existing.qty = (existing.qty || 1) + qty;
          else spares.push({ ...item, qty });
          moved += qty;
        });
        if (spares.length === 0) delete this.selections[spareIdx];
      }

      const picks = n => `${n} ${n === 1 ? 'pick' : 'picks'}`;
      const slots = `${max} ${max === 1 ? 'slot' : 'slots'}`;
      this._slotNotice = moved
        ? `Your new base has ${slots}, so we moved ${picks(moved)} to your spares${moved < removed ? ` and took off ${picks(removed - moved)}` : ''}.`
        : `Your new base has ${slots}, so we took off ${picks(removed)}.`;
    });

    // If current step just became hidden, move to nearest visible step
    if (!this._isStepVisible(this.currentStep)) {
      this.currentStep = this._nextVisibleStep(this.currentStep)
        ?? this._prevVisibleStep(this.currentStep)
        ?? 0;
    }

    this._render();
  }

  _goToStep(index) {
    if (index < 0 || index >= this.config.steps.length) return;
    this.currentStep = index;
    this._render();
    const target = this._progressEl || this.el;
    const headerGroup = document.querySelector('#header-group.sticky-top') || document.querySelector('#header-group');
    const headerHeight = headerGroup ? headerGroup.getBoundingClientRect().height : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  async _addToCart() {
    if (this._cartAddUncertain) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      return;
    }
    if (this._isAdding) return;
    if (!this._allRequiredComplete()) return;
    this._isAdding = true;
    const atcBtns = this.el.querySelectorAll('[data-builder-atc]');
    atcBtns.forEach(btn => {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    });

    // Keycap lines carry a visible Placement ("Slot 1" / "Spare"), shown in the cart, checkout, emails and admin.
    const items = Object.entries(this.selections)
      .filter(([i]) => this._isStepVisible(+i))
      .flatMap(([i, sels]) => sels.map(sel => {
        const placement = this._slotNote(this.config.steps[+i], sel);
        return {
          id: sel.variant_id,
          quantity: sel.qty || 1,
          properties: {
            _build_id: this.buildId,
            _build_name: this.config.title,
            _part: this.config.steps[+i].title,
            ...(placement ? { Placement: placement } : {})
          }
        };
      }));

    if (this.isBundle && this.config.base_variant_id) {
      items.unshift({
        id: this.config.base_variant_id,
        quantity: 1,
        properties: {
          _build_id: this.buildId,
          _build_name: this.config.title,
          _part: 'Bundle'
        }
      });
    }
    const completedPayload = this._completedPayload(items);
    let addResponse;

    try {
      const cartEl = document.querySelector('cart-container');
      const mutation = cartEl?.beginMutation?.();
      const res = addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections: BUILDER_CART_SECTIONS, sections_url: '/' })
      });

      if (!res.ok) throw new Error(`Cart error: ${res.status}`);
      const data = await res.json();

      const currentCart = document.querySelector('cart-container');
      if (currentCart) {
        if (!data.sections) {
          throw new Error('Cart was updated, but could not refresh it.');
        }
        let replacement;
        if (mutation != null && typeof currentCart.applyMutation === 'function') {
          replacement = await currentCart.applyMutation(mutation, data.sections);
        } else {
          replacement = await currentCart.reloadSections(data.sections);
        }
        if (!replacement) throw new Error('Cart was updated, but could not refresh it.');
      }

      window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }));
      window.dispatchEvent(new CustomEvent('ws:track', {
        detail: { type: 'builder_completed', payload: completedPayload }
      }));

      const cartOffcanvas = document.getElementById('offcanvas-cart');
      if (cartOffcanvas) {
        window.bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvas).show();
      }

      if (this._progressEl) this._progressEl.innerHTML = '';
      if (this._stepIntroEl) this._stepIntroEl.innerHTML = '';
      this.el.classList.add('product-builder--added');
      if (this._totalBarEl) this._totalBarEl.innerHTML = '';

      if (this._stepEl) {
        this._stepEl.innerHTML = `<div class="builder-success-state" role="status">
          <span class="builder-success-state__check" aria-hidden="true">✓</span>
          <span class="builder-success-state__kicker">Nice picks</span>
          <h2 class="builder-success-state__title">Your bundle is in the cart</h2>
          <p class="builder-success-message">It’s grouped together and ready for checkout.</p>
          <button class="builder-start-again" type="button" data-builder-restart>Build another</button>
        </div>`;
        this._stepEl.querySelector('[data-builder-restart]')?.addEventListener('click', () => this._reset());
      }

      if (this._summaryEl) {
        this._summaryEl.innerHTML = '';
      } else {
        this._reset();
      }

      if (this._mobileSummaryEl) {
        this._mobileSummaryExpanded = false;
        this.el.classList.remove('product-builder--has-mobile-tray', 'product-builder--mobile-summary-expanded');
        this._mobileSummaryEl.innerHTML = '';
      }

    } catch (err) {
      console.error('Builder ATC error:', err);
      this._cartAddUncertain = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Could not check cart');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Could not check cart');
        this._cartAddUncertain = !addResponse || addResponse.ok || addResponse.status >= 500 || cart.items.some(item => item.properties?._build_id === this.buildId);
      } catch { /* Keep retry blocked until the customer checks their cart. */ }
      this._isAdding = false;
      atcBtns.forEach(btn => {
        btn.disabled = false;
        btn.textContent = this._cartAddUncertain ? 'Check cart' : 'Add to Cart';
        btn.setAttribute('aria-live', 'polite');
      });
    }
  }
}

function builderRoots(scope) {
  const roots = [];
  if (scope.matches?.('[data-product-builder]')) roots.push(scope);
  scope.querySelectorAll?.('[data-product-builder]').forEach(root => roots.push(root));
  return roots;
}

function initBuilders(scope = document) {
  builderRoots(scope).forEach(root => {
    if (builders.has(root)) return;
    builders.set(root, new ProductBuilder(root));
  });
}

if (typeof document !== 'undefined') {
  const initialise = () => initBuilders();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();

  document.addEventListener('shopify:section:load', event => initBuilders(event.target));
  document.addEventListener('shopify:section:unload', event => {
    builderRoots(event.target).forEach(root => {
      builders.get(root)?.destroy();
      builders.delete(root);
    });
  });
}

// Global remove-build handler - works from any page's cart drawer
if (typeof document !== 'undefined') document.addEventListener('click', function(e) {
  const btn = e.target.closest('[data-remove-build]');
  if (!btn) return;
  const buildId = btn.dataset.removeBuild;
  btn.disabled = true;
  const cartEl = document.querySelector('cart-container');
  const mutation = cartEl?.beginMutation?.();

  fetch('/cart.js')
    .then(r => {
      if (!r.ok) throw new Error(`Cart error: ${r.status}`);
      return r.json();
    })
    .then(cart => {
      const updates = {};
      cart.items.forEach(item => {
        if (item.properties && item.properties['_build_id'] === buildId) {
          updates[item.key] = 0;
        }
      });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: BUILDER_CART_SECTIONS, sections_url: '/' })
      });
    })
    .then(r => {
      if (!r.ok) throw new Error(`Cart update error: ${r.status}`);
      return r.json();
    })
    .then(async data => {
      if (!data) throw new Error('Cart update failed');
      const currentCart = document.querySelector('cart-container');
      if (currentCart) {
        if (!data.sections) {
          btn.textContent = 'Cart updated — refresh page';
          return;
        }
        let replacement;
        if (mutation != null && typeof currentCart.applyMutation === 'function') {
          replacement = await currentCart.applyMutation(mutation, data.sections);
        } else {
          replacement = await currentCart.reloadSections(data.sections);
        }
        if (replacement === false) {
          btn.textContent = 'Cart updated';
          return;
        }
        if (!replacement) {
          btn.textContent = 'Cart updated — refresh page';
          return;
        }
      }
      window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data }));
    })
    .catch(() => { btn.disabled = false; });
});

if (typeof module !== 'undefined') module.exports = { ProductBuilder };
